/**
 * Shell-side access to the boot runtime (bridge + driver) and the table actions the screens
 * trigger. Errors never throw into React: they toast.
 */
import { useEffect, useState } from 'react';
import { type AppRuntime, boot, getRuntime } from '../bridge/boot';

/** Re-exported so shell modules reach the runtime through one seam. */
export { getRuntime };
import { type IntentApi, makeIntents } from '../net/intents';
import type { ConnectOptions } from '../net/TableDriver';
import { store } from '../store/store';
import { toast } from './ui';

/** Re-renders once boot resolves. */
export function useRuntime(): AppRuntime | null {
  const [rt, setRt] = useState<AppRuntime | null>(getRuntime());
  useEffect(() => {
    let alive = true;
    boot()
      .then((r) => alive && setRt(r))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return rt;
}

let intentsCache: { driver: unknown; api: IntentApi } | null = null;

export function intents(): IntentApi | null {
  const rt = getRuntime();
  if (!rt) return null;
  if (!intentsCache || intentsCache.driver !== rt.driver) intentsCache = { driver: rt.driver, api: makeIntents(rt.driver) };
  return intentsCache.api;
}

let connecting: Promise<boolean> | null = null;

/** Connect the runtime driver; resolves true on success. Concurrent calls share one attempt. */
export function connectTable(opts: ConnectOptions = {}): Promise<boolean> {
  if (connecting) return connecting;
  const rt = getRuntime();
  if (!rt) {
    toast('Still booting…', 'error');
    return Promise.resolve(false);
  }
  // Clear any previous refusal as the new attempt starts, so a stale banner cannot outlive it.
  store.getState().setSession({ connection: 'connecting', joinFailure: null });
  connecting = rt.driver
    .connect(opts)
    .then(() => true)
    .catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      // A code the server refused is not a lost connection. The socket was fine and the answer was
      // "no such table", so it gets its own state: the banner can then name the code and offer the
      // way out, rather than claiming the connection dropped and offering a retry with no code to
      // retry. Only a join can fail this way — quick play and create have no code to be wrong.
      if (opts.code && isUnknownTable(msg)) {
        store.getState().setSession({ connection: 'joinFailed', joinFailure: { code: opts.code, kind: 'unknown' } });
        return false;
      }
      store.getState().setSession({ connection: 'lost' });
      toast(friendlyConnectError(msg), 'error', 3200);
      return false;
    })
    .finally(() => {
      connecting = null;
    });
  return connecting;
}

/**
 * Leave the table and go back to the menu, now.
 *
 * Lives here rather than on MatchEnd because three screens need it: the end-of-match Leave, the
 * lobby's "Leave table" (so a player can go and join a different one), and the Leave game action in
 * Settings. The vote goes first so the room hands the seat to a bot rather than leaving three
 * players a gap, then the driver actually leaves and the shell resets to the menu. Order matters:
 * leaving first would drop the socket before the vote could travel.
 *
 * The URL is scrubbed of its join code on the way out. `Lobby`'s `attemptedCodes` guard already
 * stops a stale `?join=` re-firing within a page load; dropping it from the address bar means a
 * reload after leaving does not drag the player back to the table they just walked away from.
 */
export function leaveToMenu(): void {
  intents()?.rematchVote('leave');
  getRuntime()?.driver.leave();
  const st = store.getState();
  st.setTable(null);
  st.setPrivateView(null);
  st.setSession({ seat: null, connection: 'idle' });
  st.setPresentation({ screen: 'lobby', lastMatchResult: null, lastHandResult: null });
  clearJoinParams();
}

/** Drop `?join=` / `?code=` from the address bar. Never throws: a blocked history API costs nothing. */
function clearJoinParams(): void {
  try {
    const url = new URL(location.href);
    if (!url.searchParams.has('join') && !url.searchParams.has('code')) return;
    url.searchParams.delete('join');
    url.searchParams.delete('code');
    history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* history blocked; the guard in Lobby still holds */
  }
}

/**
 * Did the server refuse the code, as opposed to never answering?
 *
 * Deliberately narrow. Anything it does not recognise stays a lost connection, because telling a
 * player their code is wrong when the real problem was the network sends them hunting for a typo
 * that is not there.
 */
export function isUnknownTable(msg: string): boolean {
  return /no table with that code|table not found|\bnot found\b|\b404\b/i.test(msg);
}

export function friendlyConnectError(msg: string): string {
  if (/No table with that code/i.test(msg)) return 'No table with that code';
  if (/not found|404/i.test(msg)) return 'Table not found';
  if (/unauthori|401|403/i.test(msg)) return 'Sign-in expired, reload to continue';
  if (/network|fetch|websocket|ECONN|timeout/i.test(msg)) return "Can't reach the table server";
  return `Couldn't join: ${msg}`;
}

/** Audio hooks the audio agent exposes under e2e / at runtime; every call is optional. */
interface AudioHooks {
  unlock?: () => void;
  tryUnlock?: () => unknown;
  setVolume?: (which: 'master' | 'sfx' | 'music' | 'announcer', v: number) => void;
}

export function audioHooks(): AudioHooks | null {
  const w = window as unknown as { __spades?: { audio?: AudioHooks } };
  return w.__spades?.audio ?? null;
}

export function e2eParam(name: string): string | null {
  const p = new URLSearchParams(location.search);
  return p.get('e2e') === '1' ? p.get(name) : null;
}
